import { Head, usePage } from '@inertiajs/react'
import {
  ActionIcon,
  Anchor,
  Breadcrumbs,
  Checkbox,
  Group,
  Menu,
  NavLink,
  ScrollArea,
  Stack,
  Table,
  Text,
} from '@mantine/core'
import {
  Clock,
  FileText,
  Folder,
  FolderOpen,
  MoreHorizontal,
  Plus,
  Star,
  Download,
  Trash2,
} from 'lucide-react'
import type { ReactNode } from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import AppLayout from '@/components/AppLayout'
import SidebarLayout from '@/components/SidebarLayout'
import type { ProjectFilesPageProps } from '@/types'

type FileEntry = {
  name: string
  type: 'file' | 'directory'
  size: number | null
  mtime: string
}

type FileListResponse = {
  path: string
  entries: FileEntry[]
}

const ROOT_PATH = '/'

function normalizePath(path: string): string {
  const trimmed = path.trim()
  if (!trimmed || trimmed === '/') return ROOT_PATH
  return trimmed.startsWith('/') ? trimmed : `/${trimmed}`
}

function joinPath(basePath: string, name: string): string {
  const base = normalizePath(basePath)
  if (base === ROOT_PATH) return `/${name}`
  return `${base}/${name}`
}

function formatBytes(size: number | null): string {
  if (size === null) return '-'
  if (size < 1024) return `${size} B`
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`
  return `${(size / (1024 * 1024)).toFixed(1)} MB`
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr)
  const now = new Date()
  const diff = now.getTime() - date.getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 60) return `${mins} minutes ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours} hours ago`
  const days = Math.floor(hours / 24)
  if (days < 7) return `${days} days ago`
  return date.toLocaleDateString()
}

function buildBreadcrumbs(path: string): Array<{ label: string; path: string }> {
  const normalized = normalizePath(path)
  if (normalized === ROOT_PATH) {
    return [{ label: 'All files', path: ROOT_PATH }]
  }
  const segments = normalized.split('/').filter(Boolean)
  const crumbs: Array<{ label: string; path: string }> = [{ label: 'All files', path: ROOT_PATH }]
  let current = ''
  for (const segment of segments) {
    current = `${current}/${segment}`
    crumbs.push({ label: segment, path: current })
  }
  return crumbs
}

function ProjectFilesIndex() {
  const { project, csrf_token } = usePage<ProjectFilesPageProps>().props

  const [currentPath, setCurrentPath] = useState<string>(ROOT_PATH)
  const [entries, setEntries] = useState<FileEntry[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const [selectedNames, setSelectedNames] = useState<Set<string>>(new Set())
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const breadcrumbs = useMemo(() => buildBreadcrumbs(currentPath), [currentPath])

  const sortedEntries = useMemo(() => {
    const dirs = entries.filter((e) => e.type === 'directory')
    const files = entries.filter((e) => e.type === 'file')
    return [...dirs, ...files]
  }, [entries])

  const allSelected = sortedEntries.length > 0 && sortedEntries.every((e) => selectedNames.has(e.name))

  const fetchDirectory = useCallback(async (path: string) => {
    const normalizedPath = normalizePath(path)
    setLoading(true)
    setError(null)
    setSelectedNames(new Set())

    try {
      const response = await fetch(
        `/api/v1/projects/${project.id}/files?path=${encodeURIComponent(normalizedPath)}`,
        { headers: { Accept: 'application/json' } },
      )
      const payload = (await response.json()) as FileListResponse & { error?: string }
      if (!response.ok) throw new Error(payload.error || 'Failed to load files')
      setEntries(Array.isArray(payload.entries) ? payload.entries : [])
      setCurrentPath(normalizePath(payload.path || normalizedPath))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load files')
    } finally {
      setLoading(false)
    }
  }, [project.id])

  useEffect(() => {
    void fetchDirectory(ROOT_PATH)
  }, [fetchDirectory])

  const uploadFiles = useCallback(async (files: FileList | File[]) => {
    const list = Array.from(files)
    if (list.length === 0) return

    setUploading(true)
    setError(null)

    try {
      const formData = new FormData()
      list.forEach((file) => formData.append('files[]', file))

      const response = await fetch(
        `/api/v1/projects/${project.id}/files?path=${encodeURIComponent(currentPath)}`,
        {
          method: 'POST',
          body: formData,
          headers: { 'X-CSRF-Token': csrf_token || '' },
        },
      )
      const payload = (await response.json()) as { error?: string }
      if (!response.ok) throw new Error(payload.error || 'Failed to upload files')
      await fetchDirectory(currentPath)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to upload files')
    } finally {
      setUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }, [csrf_token, currentPath, fetchDirectory, project.id])

  const deletePath = useCallback(async (path: string) => {
    const confirmed = window.confirm(`Delete ${path}?`)
    if (!confirmed) return

    setError(null)
    try {
      const response = await fetch(
        `/api/v1/projects/${project.id}/files?path=${encodeURIComponent(path)}`,
        { method: 'DELETE', headers: { 'X-CSRF-Token': csrf_token || '' } },
      )
      if (!response.ok) {
        const payload = (await response.json()) as { error?: string }
        throw new Error(payload.error || 'Failed to delete')
      }
      await fetchDirectory(currentPath)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to delete')
    }
  }, [csrf_token, currentPath, fetchDirectory, project.id])

  const toggleAll = () => {
    if (allSelected) {
      setSelectedNames(new Set())
    } else {
      setSelectedNames(new Set(sortedEntries.map((e) => e.name)))
    }
  }

  const toggleOne = (name: string) => {
    setSelectedNames((prev) => {
      const next = new Set(prev)
      if (next.has(name)) next.delete(name)
      else next.add(name)
      return next
    })
  }

  return (
    <>
      <Head title={`Files - ${project.name}`} />

      <SidebarLayout sidebarWidth={240} sidebar={({ closeSidebar }) => (
        <>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--mantine-color-gray-3)' }}>
            <Text fw={600} size="sm" lineClamp={1}>{project.name}</Text>
          </div>

          <Stack gap={0} p="sm">
            <NavLink
              label="All files"
              leftSection={<FolderOpen size={18} strokeWidth={1.5} />}
              active
              onClick={() => { void fetchDirectory(ROOT_PATH); closeSidebar(); }}
            />
            <NavLink
              label="Recent"
              leftSection={<Clock size={18} strokeWidth={1.5} />}
              disabled
            />
            <NavLink
              label="Favorites"
              leftSection={<Star size={18} strokeWidth={1.5} />}
              disabled
            />
          </Stack>
        </>
      )}>
          {/* Toolbar */}
          <div
            style={{
              padding: '8px 16px',
              borderBottom: '1px solid var(--mantine-color-gray-3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Breadcrumbs separator="/">
              {breadcrumbs.map((crumb) => (
                <Anchor
                  key={crumb.path}
                  component="button"
                  type="button"
                  size="sm"
                  onClick={() => void fetchDirectory(crumb.path)}
                >
                  {crumb.label}
                </Anchor>
              ))}
            </Breadcrumbs>

            <Group gap="xs">
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={(e) => {
                  if (e.target.files) void uploadFiles(e.target.files)
                }}
                hidden
              />
              <ActionIcon
                variant="light"
                size="md"
                onClick={() => fileInputRef.current?.click()}
                loading={uploading}
                title="Upload files"
              >
                <Plus size={16} />
              </ActionIcon>
            </Group>
          </div>

          {/* Error message */}
          {error && (
            <div style={{ padding: '8px 16px' }}>
              <Text c="red" size="sm">{error}</Text>
            </div>
          )}

          {/* File list */}
          <ScrollArea style={{ flex: 1 }}>
            <Table highlightOnHover>
              <Table.Thead>
                <Table.Tr style={{ backgroundColor: 'var(--mantine-color-gray-0)' }}>
                  <Table.Th style={{ width: 40 }}>
                    <Checkbox
                      checked={allSelected}
                      indeterminate={selectedNames.size > 0 && !allSelected}
                      onChange={toggleAll}
                      aria-label="Select all files"
                    />
                  </Table.Th>
                  <Table.Th>Name</Table.Th>
                  <Table.Th style={{ width: 48 }} />
                  <Table.Th style={{ width: 100 }}>Size</Table.Th>
                  <Table.Th style={{ width: 160 }}>Modified</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {loading ? (
                  <Table.Tr>
                    <Table.Td colSpan={5}>
                      <Text c="dimmed" size="sm" py="xl" ta="center">Loading...</Text>
                    </Table.Td>
                  </Table.Tr>
                ) : sortedEntries.length === 0 ? (
                  <Table.Tr>
                    <Table.Td colSpan={5}>
                      <Text c="dimmed" size="sm" py="xl" ta="center">No files in this directory.</Text>
                    </Table.Td>
                  </Table.Tr>
                ) : (
                  sortedEntries.map((entry) => {
                    const absolutePath = joinPath(currentPath, entry.name)
                    const isDir = entry.type === 'directory'

                    return (
                      <Table.Tr key={entry.name}>
                        <Table.Td>
                          <Checkbox
                            checked={selectedNames.has(entry.name)}
                            onChange={() => toggleOne(entry.name)}
                            onClick={(e) => e.stopPropagation()}
                            aria-label={`Select ${entry.name}`}
                          />
                        </Table.Td>
                        <Table.Td>
                          <Group gap="sm" wrap="nowrap">
                            {isDir ? (
                              <Folder size={18} color="var(--mantine-color-yellow-6)" />
                            ) : (
                              <FileText size={18} color="var(--mantine-color-gray-5)" />
                            )}
                            {isDir ? (
                              <Anchor
                                component="button"
                                type="button"
                                size="sm"
                                fw={500}
                                onClick={() => void fetchDirectory(absolutePath)}
                              >
                                {entry.name}
                              </Anchor>
                            ) : (
                              <Text size="sm">{entry.name}</Text>
                            )}
                          </Group>
                        </Table.Td>
                        <Table.Td>
                          <Menu shadow="md" width={160} position="bottom-end">
                            <Menu.Target>
                              <ActionIcon variant="subtle" size="sm" color="gray">
                                <MoreHorizontal size={16} />
                              </ActionIcon>
                            </Menu.Target>
                            <Menu.Dropdown>
                              {!isDir && (
                                <Menu.Item
                                  leftSection={<Download size={14} />}
                                  component="a"
                                  href={`/api/v1/projects/${project.id}/files/download?path=${encodeURIComponent(absolutePath)}`}
                                >
                                  Download
                                </Menu.Item>
                              )}
                              <Menu.Item
                                leftSection={<Trash2 size={14} />}
                                color="red"
                                onClick={() => void deletePath(absolutePath)}
                              >
                                Delete
                              </Menu.Item>
                            </Menu.Dropdown>
                          </Menu>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" c="dimmed">{isDir ? '-' : formatBytes(entry.size)}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" c="dimmed">{formatDate(entry.mtime)}</Text>
                        </Table.Td>
                      </Table.Tr>
                    )
                  })
                )}
              </Table.Tbody>
            </Table>
          </ScrollArea>
      </SidebarLayout>
    </>
  )
}

ProjectFilesIndex.layout = (page: ReactNode) => <AppLayout padding={0}>{page}</AppLayout>

export default ProjectFilesIndex
